import { useState, useEffect, useCallback, useRef } from "react";
import ArchitectureOutlinedIcon from "@mui/icons-material/ArchitectureOutlined";
import VideoLibraryOutlinedIcon from "@mui/icons-material/VideoLibraryOutlined";
import LinkOutlinedIcon from "@mui/icons-material/LinkOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";

const AUTO_INTERVAL_MS = 5000;

const STEPS = [
  {
    icon: <ArchitectureOutlinedIcon />,
    title: "Спроектируйте программу обучения",
    desc: "Определите, из каких уроков состоит сценарий и в каком порядке сотрудники будут их проходить.",
    example: {
      label: "Пример: адаптация новичка",
      items: [
        "Урок 1 — Знакомство с компанией",
        "Урок 2 — Продукт и процессы",
        "Урок 3 — Проверка знаний",
      ],
    },
  },
  {
    icon: <VideoLibraryOutlinedIcon />,
    title: "Загрузите видео и настройте сценарий",
    desc: "Запишите уроки, загрузите их в редактор и свяжите сцены кнопками выбора — сотрудник сам решает, куда перейти дальше.",
    example: {
      label: "В редакторе вы",
      items: [
        "Добавляете сцену и загружаете видео",
        "Настраиваете кнопки «Дальше» и «Повторить»",
        "Собираете маршрут прохождения на карте",
      ],
    },
  },
  {
    icon: <LinkOutlinedIcon />,
    title: "Подключите подписку и поделитесь ссылкой",
    desc: "Оформите тариф по размеру команды и отправьте ссылку сотрудникам — они начнут прохождение в выбранном мессенджере.",
    example: {
      label: "Для команды",
      items: [
        "Одна ссылка на весь отдел",
        "Без персональных ключей",
        "Подписка по числу сотрудников",
      ],
    },
  },
  {
    icon: <RocketLaunchOutlinedIcon />,
    title: "Масштабируйте обучение",
    desc: "Один раз собрали сценарий — запускайте его снова и снова для новых сотрудников без дополнительной работы.",
    example: {
      label: "Результат для бизнеса",
      items: [
        "Быстрее онбординг новичков",
        "Меньше нагрузки на наставников",
        "Один сценарий — сотни прохождений",
      ],
    },
  },
];

export default function CustomizedTimeline() {
  const [activeStep, setActiveStep] = useState(0);
  const pausedRef = useRef(false);

  const goToStep = useCallback((index) => {
    setActiveStep(index);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      if (!pausedRef.current) {
        setActiveStep((prev) => (prev + 1) % STEPS.length);
      }
    }, AUTO_INTERVAL_MS);

    return () => clearInterval(timer);
  }, []);

  const step = STEPS[activeStep];

  return (
    <div
      className="steps-widget"
      onMouseEnter={() => {
        pausedRef.current = true;
      }}
      onMouseLeave={() => {
        pausedRef.current = false;
      }}
    >
      <div className="steps-widget__nav">
        {STEPS.map(({ title }, index) => (
          <button
            key={title}
            type="button"
            className={`steps-widget__step${
              index === activeStep ? " steps-widget__step--active" : ""
            }${index < activeStep ? " steps-widget__step--done" : ""}`}
            onClick={() => goToStep(index)}
            aria-current={index === activeStep ? "step" : undefined}
          >
            <span className="steps-widget__step-num">{index + 1}</span>
            <span className="steps-widget__step-label">{title}</span>
          </button>
        ))}
      </div>

      <div className="steps-widget__progress">
        <div
          className="steps-widget__progress-bar"
          style={{ width: `${((activeStep + 1) / STEPS.length) * 100}%` }}
        />
      </div>

      <div className="steps-widget__panel" key={activeStep}>
        <div className="steps-widget__panel-icon">{step.icon}</div>
        <div className="steps-widget__panel-body">
          <h3 className="steps-widget__panel-title">{step.title}</h3>
          <p className="steps-widget__panel-desc">{step.desc}</p>
          <div className="steps-widget__example">
            <div className="steps-widget__example-label">{step.example.label}</div>
            <ul className="steps-widget__example-list">
              {step.example.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      <div className="steps-widget__dots">
        {STEPS.map((_, index) => (
          <button
            key={index}
            type="button"
            className={`steps-widget__dot${
              index === activeStep ? " steps-widget__dot--active" : ""
            }`}
            onClick={() => goToStep(index)}
            aria-label={`Шаг ${index + 1}`}
          />
        ))}
      </div>
    </div>
  );
}
